import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Clock,
  Lock,
  Plus,
  ShieldCheck,
  Target,
  Trash2,
  Unlock,
  UserPlus,
  X,
  SlidersHorizontal,
} from 'lucide-react';
import { gauntlet, ApiError, type Profile, type ProfileRole } from "../lib/api";
import { cx, formatFiat } from "../lib/format";
import {
  Badge,
  Button,
  Callout,
  EmptyState,
  Input,
  Modal,
  Panel,
  Skeleton,
} from "../components/ui";

/** What each role can do, in words a parent reads rather than flags. */
const ROLE_BLURB: Record<ProfileRole, string> = {
  guardian: "Full control. Can approve requests and change everyone's limits.",
  teen: "Can spend within limits, and ask for more when they need it.",
  child: "Can receive and save. Every payment goes to a guardian first.",
  viewer: "Read-only. Useful for an accountant or a partner.",
};

export function FamilyPage() {
  const queryClient = useQueryClient();
  const [addingProfile, setAddingProfile] = useState(false);
  const [switchingTo, setSwitchingTo] = useState<Profile | null>(null);
  const [editing, setEditing] = useState<Profile | null>(null);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState<string | null>(null);
  // A PIN-less switch has no modal to show an error in, so failures need
  // their own surface on the page itself.
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Profile | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["household"],
    queryFn: () => gauntlet.household(),
    refetchInterval: 30_000,
  });

  const { data: approvalData } = useQuery({
    queryKey: ["approvals"],
    queryFn: () => gauntlet.approvals(),
    refetchInterval: 15_000,
  });

  const { data: allowanceData } = useQuery({
    queryKey: ["allowances"],
    queryFn: () => gauntlet.allowances(),
  });

  const remove = useMutation({
    mutationFn: (id: number) => gauntlet.archiveProfile(id),
    onSuccess: async () => {
      setRemoving(null);
      setRemoveError(null);
      await queryClient.invalidateQueries();
    },
    onError: (err) =>
      setRemoveError(
        err instanceof ApiError ? err.message : "Could not remove that profile",
      ),
  });

  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: number; approve: boolean }) =>
      gauntlet.decideApproval(id, approve),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const switchProfile = useMutation({
    mutationFn: ({ id, pin: code }: { id: number; pin?: string }) =>
      gauntlet.switchProfile(id, code),
    onSuccess: async () => {
      setSwitchingTo(null);
      setPin("");
      setPinError(null);
      await queryClient.invalidateQueries();
    },
    onError: (error) => {
      const message =
        error instanceof ApiError ? error.message : "Could not switch profile";
      setPinError(message);
      setSwitchError(message);
    },
  });

  if (isLoading) return <Skeleton className="h-96 w-full" />;

  const profiles = data?.profiles ?? [];
  const active = profiles.find((p) => p.id === data?.activeProfileId);
  const isGuardian = active?.role === "guardian";
  const pending = approvalData?.pending ?? [];
  const mine = approvalData?.mine ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">
            {data?.household.name}
          </h1>
          <p className="mt-1 text-sm text-ink-dim">
            {profiles.length} profile{profiles.length === 1 ? "" : "s"}
            {active && ` · you are ${active.name}`}
          </p>
        </div>
        {isGuardian && (
          <Button
            variant="primary"
            icon={<UserPlus size={15} />}
            onClick={() => setAddingProfile(true)}
          >
            Add profile
          </Button>
        )}
      </div>

      {/* Requests waiting on a guardian */}
      {isGuardian && pending.length > 0 && (
        <Panel
          title={`${pending.length} request${pending.length === 1 ? "" : "s"} waiting for you`}
          padded={false}
        >
          <ul className="divide-y divide-hairline">
            {pending.map((request) => (
              <li
                key={request.id}
                className="flex flex-wrap items-center gap-3 px-5 py-3.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm">
                    <span className="font-medium">{request.profile_name}</span>{" "}
                    <span className="text-ink-dim">
                      {request.reason ?? request.kind}
                    </span>
                  </p>
                  {request.amount_cents && (
                    <p className="num text-xs text-ink-faint">
                      up to {formatFiat(request.amount_cents)}
                    </p>
                  )}
                </div>
                <div className="flex gap-1.5">
                  <Button
                    size="sm"
                    variant="danger"
                    icon={<X size={13} />}
                    onClick={() =>
                      decide.mutate({ id: request.id, approve: false })
                    }
                  >
                    Decline
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<Check size={13} />}
                    onClick={() =>
                      decide.mutate({ id: request.id, approve: true })
                    }
                  >
                    Approve
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* A child's own pending requests */}
      {!isGuardian && mine.length > 0 && (
        <Panel title="Your requests" padded={false}>
          <ul className="divide-y divide-hairline">
            {mine.slice(0, 6).map((request) => (
              <li
                key={request.id}
                className="flex items-center gap-3 px-5 py-3"
              >
                <Clock size={14} className="shrink-0 text-ink-faint" />
                <span className="min-w-0 flex-1 truncate text-sm">
                  {request.reason ?? request.kind}
                </span>
                <Badge
                  tone={
                    request.status === "approved"
                      ? "gain"
                      : request.status === "declined"
                        ? "loss"
                        : "warn"
                  }
                >
                  {request.status}
                </Badge>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {switchError && (
        <Callout tone="danger" title="Could not switch profile">
          {switchError}
        </Callout>
      )}

      {/* Profiles */}
      <Panel title="Profiles" padded={false}>
        <ul className="divide-y divide-hairline">
          {profiles.map((profile) => (
            <li
              key={profile.id}
              className="flex flex-wrap items-center gap-3 px-5 py-4"
            >
              <Avatar profile={profile} />

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{profile.name}</span>
                  <Badge
                    tone={profile.role === "guardian" ? "brass" : "neutral"}
                  >
                    {profile.role}
                  </Badge>
                  {profile.age !== null && <Badge>{profile.age}</Badge>}
                  {profile.locked === 1 && <Badge tone="loss">locked</Badge>}
                  {profile.id === data?.activeProfileId && (
                    <Badge tone="gain">you</Badge>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-ink-faint">
                  {profile.can_send
                    ? profile.per_tx_limit_cents
                      ? `Up to ${formatFiat(profile.per_tx_limit_cents)} per payment`
                      : "No spending limit"
                    : "Needs approval to spend"}
                  {profile.spentThisWeek &&
                    Number(profile.spentThisWeek) > 0 && (
                      <> · {formatFiat(profile.spentThisWeek)} this week</>
                    )}
                </p>
              </div>

              <div className="flex gap-1.5">
                {isGuardian && profile.locked === 1 && (
                  <Button
                    size="sm"
                    icon={<Unlock size={13} />}
                    onClick={async () => {
                      await gauntlet.unlockProfile(profile.id);
                      await queryClient.invalidateQueries();
                    }}
                  >
                    Unlock
                  </Button>
                )}
                {profile.id !== data?.activeProfileId &&
                  profile.locked === 0 && (
                    <Button
                      size="sm"
                      icon={profile.has_pin ? <Lock size={13} /> : undefined}
                      onClick={() => {
                        if (profile.has_pin) setSwitchingTo(profile);
                        else switchProfile.mutate({ id: profile.id });
                      }}
                    >
                      Switch
                    </Button>
                  )}

                {/* Without this the household is a dead end: Cards tells the
                    user to "turn on can use card", and until now there was
                    nowhere in the app to do that. */}
                {isGuardian && (
                  <Button
                    size="sm"
                    icon={<SlidersHorizontal size={13} />}
                    onClick={() => setEditing(profile)}
                  >
                    Edit
                  </Button>
                )}

                {/* A guardian action, and never offered on the profile you are
                    currently using — removing yourself mid-session would leave
                    the app with no acting profile. The server separately
                    refuses to remove the last guardian. */}
                {isGuardian && profile.id !== data?.activeProfileId && (
                  <Button
                    size="sm"
                    variant="danger"
                    icon={<Trash2 size={13} />}
                    onClick={() => {
                      setRemoveError(null);
                      setRemoving(profile);
                    }}
                    aria-label={`Remove ${profile.name}`}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Panel>

      {/* Allowances */}
      {isGuardian && (
        <Panel
          title="Allowances"
          padded={false}
          action={
            <AllowanceButton
              profiles={profiles}
              onDone={() => queryClient.invalidateQueries()}
            />
          }
        >
          {(allowanceData?.allowances.length ?? 0) === 0 ? (
            <EmptyState
              title="No allowances set up"
              description="Schedule recurring pocket money. Gauntlet prepares the transfer and asks you to confirm — it never moves money on a timer by itself."
            />
          ) : (
            <ul className="divide-y divide-hairline">
              {allowanceData?.allowances.map((allowance) => (
                <li
                  key={allowance.id}
                  className="flex items-center gap-3 px-5 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm">
                      <span className="font-medium">
                        {allowance.profile_name}
                      </span>
                      <span className="num ml-2">
                        {formatFiat(allowance.amount_cents)}
                      </span>
                      <span className="text-ink-faint">
                        {" "}
                        {allowance.frequency}
                      </span>
                    </p>
                    <p className="text-xs text-ink-faint">
                      next {new Date(allowance.next_run).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      await gauntlet.deleteAllowance(allowance.id);
                      await queryClient.invalidateQueries();
                    }}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}

      <GoalsPanel profileId={active?.id} />

      {/* Switch with a PIN */}
      <Modal
        open={switchingTo !== null}
        onClose={() => {
          setSwitchingTo(null);
          setPin("");
          setPinError(null);
        }}
        title={`Switch to ${switchingTo?.name ?? ""}`}
      >
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (switchingTo) switchProfile.mutate({ id: switchingTo.id, pin });
          }}
        >
          <Input
            label="PIN"
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) =>
              setPin(e.target.value.replace(/\D/g, "").slice(0, 12))
            }
            mono
            autoFocus
            error={pinError}
          />
          <Button
            variant="primary"
            full
            type="submit"
            loading={switchProfile.isPending}
          >
            Switch
          </Button>
        </form>
      </Modal>

      {/* Removal confirmation.

          Worth being precise, because "remove" reads as "delete" and the
          consequences differ: the profile's keys derive from the household seed
          and its addresses may hold coins, so the row is archived rather than
          destroyed and its BIP-44 account index is never reissued. */}
      <Modal
        open={removing !== null}
        onClose={() => {
          setRemoving(null);
          setRemoveError(null);
        }}
        title={`Remove ${removing?.name ?? ""}?`}
      >
        <div className="space-y-4">
          <p className="text-sm text-ink-dim">
            {removing?.name} will be hidden from the household and will no longer be able to sign
            in or spend.
          </p>

          <Callout tone="warn" title="Their money is not deleted">
            Any coins at {removing?.name}&apos;s addresses stay on the blockchain and remain
            recoverable from your recovery phrase — their keys come from the same seed. Move
            anything they hold first if you want it somewhere you can see it.
          </Callout>

          <ul className="space-y-1.5 text-xs text-ink-faint">
            <li>· Their transaction history is kept.</li>
            <li>· Their derivation slot is never given to a new profile.</li>
            <li>· Any cards or allowances they had stop working.</li>
          </ul>

          {removeError && <Callout tone="danger">{removeError}</Callout>}

          <div className="flex gap-2">
            <Button
              full
              onClick={() => {
                setRemoving(null);
                setRemoveError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              full
              loading={remove.isPending}
              onClick={() => removing && remove.mutate(removing.id)}
            >
              Remove profile
            </Button>
          </div>
        </div>
      </Modal>

      <AddProfileModal
        open={addingProfile}
        onClose={() => setAddingProfile(false)}
        onDone={() => queryClient.invalidateQueries()}
      />
      <EditProfileModal profile={editing} onClose={() => setEditing(null)} />

    </div>
  );
}

function Avatar({ profile }: { profile: Profile }) {
  const colour = profile.color ?? stableColour(profile.name);
  return (
    <div
      className="flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
      style={{
        background: `${colour}22`,
        color: colour,
        border: `1px solid ${colour}44`,
      }}
    >
      {profile.avatar ?? profile.name.slice(0, 1).toUpperCase()}
    </div>
  );
}

function stableColour(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return `hsl(${Math.abs(hash) % 360} 60% 60%)`;
}

function AddProfileModal({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState("");
  const [role, setRole] = useState<ProfileRole>("child");
  const [birthYear, setBirthYear] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      gauntlet.createProfile({
        name,
        role,
        ...(birthYear ? { birthYear: Number(birthYear) } : {}),
        ...(pin ? { pin } : {}),
      }),
    onSuccess: () => {
      setName("");
      setBirthYear("");
      setPin("");
      setError(null);
      onDone();
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not add profile"),
  });

  return (
    <Modal open={open} onClose={onClose} title="Add a profile">
      <div className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />

        <div className="space-y-1.5">
          <span className="block text-[13px] font-medium text-ink-dim">
            Role
          </span>
          <div className="space-y-1.5">
            {(["child", "teen", "guardian", "viewer"] as const).map(
              (option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setRole(option)}
                  className={cx(
                    "w-full rounded-lg border p-3 text-left transition-colors",
                    role === option
                      ? "border-brass/50 bg-brass-wash"
                      : "border-hairline hover:border-hairline-bright",
                  )}
                >
                  <span
                    className={cx(
                      "block text-sm font-medium capitalize",
                      role === option ? "text-brass" : "text-ink",
                    )}
                  >
                    {option}
                  </span>
                  <span className="mt-0.5 block text-xs text-ink-faint">
                    {ROLE_BLURB[option]}
                  </span>
                </button>
              ),
            )}
          </div>
        </div>

        <Input
          label="Birth year"
          value={birthYear}
          onChange={(e) =>
            setBirthYear(e.target.value.replace(/\D/g, "").slice(0, 4))
          }
          placeholder="2015"
          inputMode="numeric"
          hint="Only the year — a wallet has no need for a child's exact date of birth."
        />

        <Input
          label="PIN"
          type="password"
          value={pin}
          onChange={(e) =>
            setPin(e.target.value.replace(/\D/g, "").slice(0, 12))
          }
          inputMode="numeric"
          mono
          hint="Optional. 4–12 digits, so they can switch to their own profile."
        />

        {error && <Callout tone="danger">{error}</Callout>}

        <Callout tone="info">
          This profile gets its own set of addresses under your wallet, so money
          sent to them is genuinely theirs on-chain. Your recovery phrase still
          restores everything.
        </Callout>

        <div className="flex gap-2">
          <Button full onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            full
            disabled={!name}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Add
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AllowanceButton({
  profiles,
  onDone,
}: {
  profiles: Profile[];
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [profileId, setProfileId] = useState<number | null>(null);
  const [amount, setAmount] = useState("5.00");
  const [frequency, setFrequency] = useState<
    "daily" | "weekly" | "biweekly" | "monthly"
  >("weekly");

  const create = useMutation({
    mutationFn: () =>
      gauntlet.createAllowance({
        profileId: profileId as number,
        amountCents: String(Math.round(Number(amount) * 100)),
        frequency,
      }),
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });

  const eligible = profiles.filter(
    (p) => p.role !== "guardian" && p.role !== "viewer",
  );

  // Same dead-end as the card form had: a submit disabled on a selection the
  // user has no cue to make. Default to the only candidate when there is one.
  useEffect(() => {
    if (!open) return;
    setProfileId((current) => {
      if (current && eligible.some((p) => p.id === current)) return current;
      return eligible.length === 1 ? (eligible[0]?.id ?? null) : null;
    });
  }, [open, eligible.map((p) => p.id).join(",")]);

  return (
    <>
      <Button size="sm" icon={<Plus size={14} />} onClick={() => setOpen(true)}>
        Add
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title="New allowance">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <span className="block text-[13px] font-medium text-ink-dim">
              Who
            </span>
            <div className="flex flex-wrap gap-1.5">
              {eligible.map((profile) => (
                <button
                  key={profile.id}
                  type="button"
                  onClick={() => setProfileId(profile.id)}
                  aria-pressed={profileId === profile.id}
                  className={cx(
                    "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm transition-colors",
                    profileId === profile.id
                      ? "border-brass bg-brass-wash font-medium text-brass"
                      : "border-hairline text-ink-dim hover:border-hairline-bright hover:text-ink",
                  )}
                >
                  {profileId === profile.id && <Check size={13} />}
                  {profile.name}
                </button>
              ))}
            </div>
          </div>

          <Input
            label="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
            inputMode="decimal"
            mono
            hint="In your household currency. Converted at send time, so a promise of $5 stays $5."
          />

          <div className="space-y-1.5">
            <span className="block text-[13px] font-medium text-ink-dim">
              How often
            </span>
            <div className="grid grid-cols-4 gap-1.5">
              {(["daily", "weekly", "biweekly", "monthly"] as const).map(
                (option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFrequency(option)}
                    className={cx(
                      "rounded-lg border px-2 py-2 text-xs capitalize transition-colors",
                      frequency === option
                        ? "border-brass/50 bg-brass-wash text-brass"
                        : "border-hairline text-ink-dim",
                    )}
                  >
                    {option}
                  </button>
                ),
              )}
            </div>
          </div>

          {eligible.length === 0 && (
            <Callout tone="warn">
              An allowance goes to a child or teen profile. Add one under Family
              first.
            </Callout>
          )}

          <Button
            variant="primary"
            full
            disabled={!profileId || !(Number(amount) > 0)}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create allowance
          </Button>

          {eligible.length > 0 && !profileId && (
            <p className="text-center text-xs text-ink-faint">
              Choose who this allowance is for.
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}

function GoalsPanel({ profileId }: { profileId?: number }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [target, setTarget] = useState("50.00");
  const [emoji, setEmoji] = useState("🎯");

  const { data } = useQuery({
    queryKey: ["goals", profileId],
    queryFn: () => gauntlet.goals(),
    enabled: profileId !== undefined,
  });

  const create = useMutation({
    mutationFn: () =>
      gauntlet.createGoal({
        name,
        emoji,
        targetCents: String(Math.round(Number(target) * 100)),
      }),
    onSuccess: async () => {
      setAdding(false);
      setName("");
      await queryClient.invalidateQueries({ queryKey: ["goals"] });
    },
  });

  const [contributions, setContributions] = useState<Record<number, string>>({});

  const contribute = useMutation({
    mutationFn: ({ id, cents }: { id: number; cents: string }) =>
      gauntlet.contributeToGoal(id, cents),
    onSuccess: async (_r, vars) => {
      setContributions((prev) => ({ ...prev, [vars.id]: "" }));
      await queryClient.invalidateQueries({ queryKey: ["goals"] });
    },
  });

  const removeGoal = useMutation({
    mutationFn: (id: number) => gauntlet.deleteGoal(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["goals"] }),
  });

  const goals = data?.goals ?? [];

  return (
    <Panel
      title="Savings goals"
      padded={false}
      action={
        <Button
          size="sm"
          icon={<Target size={14} />}
          onClick={() => setAdding(true)}
        >
          New goal
        </Button>
      }
    >
      {goals.length === 0 ? (
        <EmptyState
          title="No goals yet"
          description="A goal turns an abstract balance into something a child can actually picture."
        />
      ) : (
        <ul className="divide-y divide-hairline">
          {goals.map((goal) => {
            const progress = Math.min(
              100,
              (Number(goal.saved_cents) /
                Math.max(1, Number(goal.target_cents))) *
                100,
            );
            return (
              <li key={goal.id} className="px-5 py-3.5">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{goal.emoji ?? "🎯"}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">
                        {goal.name}
                      </span>
                      {goal.achieved_at && <Badge tone="gain">reached</Badge>}
                    </div>
                    <p className="num text-xs text-ink-faint">
                      {formatFiat(goal.saved_cents)} of{" "}
                      {formatFiat(goal.target_cents)}
                    </p>
                  </div>
                  <span className="num text-sm text-ink-dim">
                    {progress.toFixed(0)}%
                  </span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-raised">
                  <div
                    className="h-full rounded-full bg-brass transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                {/* A goal you cannot pay into is a picture, not a goal. */}
                <div className="mt-2.5 flex items-center gap-2">
                  <Input
                    placeholder="Add amount"
                    value={contributions[goal.id] ?? ""}
                    onChange={(e) =>
                      setContributions((prev) => ({
                        ...prev,
                        [goal.id]: e.target.value.replace(/[^\d.]/g, ""),
                      }))
                    }
                    className="h-8 flex-1"
                    mono
                  />
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={!Number(contributions[goal.id])}
                    loading={contribute.isPending && contribute.variables?.id === goal.id}
                    onClick={() =>
                      contribute.mutate({
                        id: goal.id,
                        cents: String(
                          Math.round(Number(contributions[goal.id] ?? 0) * 100),
                        ),
                      })
                    }
                  >
                    Add
                  </Button>
                  <button
                    onClick={() => removeGoal.mutate(goal.id)}
                    className="rounded p-1.5 text-ink-faint transition-colors hover:text-loss"
                    aria-label="Delete goal"
                    title="Delete goal"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="New savings goal"
      >
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              label="Emoji"
              value={emoji}
              onChange={(e) => setEmoji(e.target.value.slice(0, 4))}
              className="w-20 text-center"
            />
            <div className="flex-1">
              <Input
                label="What for?"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="A new bike"
                autoFocus
              />
            </div>
          </div>
          <Input
            label="Target"
            value={target}
            onChange={(e) => setTarget(e.target.value.replace(/[^\d.]/g, ""))}
            inputMode="decimal"
            mono
          />
          <Button
            variant="primary"
            full
            disabled={!name}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create
          </Button>
        </div>
      </Modal>
    </Panel>
  );
}

export { ShieldCheck };


/**
 * Permissions and limits for one profile.
 *
 * Limits are held in the database as cents in a string column, but nobody
 * thinks in cents — the inputs take whole currency units and convert on the way
 * in and out. An empty box means "no limit", which is distinct from zero.
 */
function EditProfileModal({
  profile,
  onClose,
}: {
  profile: Profile | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    const units = (cents: string | null): string =>
      cents === null || cents === "" ? "" : String(Number(cents) / 100);
    setForm({
      name: profile.name,
      canSend: profile.can_send === 1,
      canReceive: profile.can_receive === 1,
      canUseCard: profile.can_use_card === 1,
      locked: profile.locked === 1,
      perTx: units(profile.per_tx_limit_cents),
      daily: units(profile.daily_limit_cents),
      weekly: units(profile.weekly_limit_cents),
      approvalOver: units(profile.approval_over_cents),
    });
    setError(null);
  }, [profile]);

  const save = useMutation({
    mutationFn: () => {
      const cents = (value: unknown): number | null => {
        const text = String(value ?? "").trim();
        return text === "" ? null : Math.round(Number(text) * 100);
      };
      return gauntlet.updateProfile(profile!.id, {
        name: form["name"],
        canSend: form["canSend"],
        canReceive: form["canReceive"],
        canUseCard: form["canUseCard"],
        locked: form["locked"],
        perTxLimitCents: cents(form["perTx"]),
        dailyLimitCents: cents(form["daily"]),
        weeklyLimitCents: cents(form["weekly"]),
        approvalOverCents: cents(form["approvalOver"]),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onClose();
    },
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : "Could not save those changes."),
  });

  const set = (key: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggles = [
    ["canSend", "Can send", "Make payments without asking a guardian first."],
    ["canReceive", "Can receive", "Has their own receive address."],
    ["canUseCard", "Can use a card", "Eligible to be issued a virtual card."],
    ["locked", "Locked out", "Blocks this profile from signing in at all."],
  ] as const;

  return (
    <Modal
      open={profile !== null}
      onClose={onClose}
      title={profile ? `Edit ${profile.name}` : ""}
      width="max-w-md"
    >
      <div className="space-y-4">
        <Input
          label="Name"
          value={String(form["name"] ?? "")}
          onChange={(e) => set("name", e.target.value)}
        />

        <div className="space-y-1.5">
          <span className="label-xs">Permissions</span>
          {toggles.map(([key, label, help]) => (
            <label
              key={key}
              className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-hairline p-2.5"
            >
              <input
                type="checkbox"
                checked={Boolean(form[key])}
                onChange={(e) => set(key, e.target.checked)}
                className={
                  key === "locked"
                    ? "mt-0.5 accent-[var(--color-loss)]"
                    : "mt-0.5 accent-[var(--color-brass)]"
                }
              />
              <span className="text-[13px]">
                <span className="font-medium">{label}</span>
                <span className="mt-0.5 block text-ink-faint">{help}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="space-y-1.5">
          <span className="label-xs">Spending limits</span>
          <p className="text-xs text-ink-faint">
            Leave a box empty for no limit. Anything over the approval threshold
            raises a request instead of being declined outright.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Input
              label="Per payment"
              value={String(form["perTx"] ?? "")}
              onChange={(e) => set("perTx", e.target.value)}
              placeholder="none"
              mono
            />
            <Input
              label="Per day"
              value={String(form["daily"] ?? "")}
              onChange={(e) => set("daily", e.target.value)}
              placeholder="none"
              mono
            />
            <Input
              label="Per week"
              value={String(form["weekly"] ?? "")}
              onChange={(e) => set("weekly", e.target.value)}
              placeholder="none"
              mono
            />
            <Input
              label="Ask a guardian over"
              value={String(form["approvalOver"] ?? "")}
              onChange={(e) => set("approvalOver", e.target.value)}
              placeholder="none"
              mono
            />
          </div>
        </div>

        {error && <Callout tone="danger">{error}</Callout>}

        <div className="flex gap-2">
          <Button full onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            full
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}
